import api from './api';

export interface TenantUser {
  id: number;
  tenant_user_id?: number; // ID du TenantUser (pour compatibilité avec différentes structures)
  user_id: number;
  tenant_id: number;
  role: 'TENANT_ADMIN' | 'TENANT_MANAGER' | 'TENANT_OWNER';
  notes?: string;
  expires_at?: string;
  created_at: string;
  updated_at?: string;
  is_active?: boolean;
  // Structure imbriquée (si disponible)
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
  };
  // Champs séparés (structure alternative du backend)
  user_username?: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_is_active?: boolean;
  tenant_name?: string;
  tenant_code?: string;
  assigned_by_username?: string;
  role_display?: string;
}

export interface AvailableUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

export interface CreateTenantUserData {
  user_id: number;
  tenant_id: number;
  role: 'TENANT_ADMIN' | 'TENANT_MANAGER';
  notes?: string;
  expires_at?: string;
}

class TenantUserService {
  private baseUrl = '/platform/tenant-users/';

  /**
   * Récupérer les utilisateurs disponibles pour un tenant
   * (utilisateurs qui ne sont pas encore assignés à ce tenant)
   */
  async getAvailableUsers(tenantId: number): Promise<AvailableUser[]> {
    try {
      const response = await api.get(`${this.baseUrl}available-users/?tenant_id=${tenantId}`);
      return response.data.available_users || response.data.data || response.data.results || response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des utilisateurs disponibles');
    }
  }

  /**
   * Ajouter un utilisateur à un tenant
   */
  async addUserToTenant(data: CreateTenantUserData): Promise<TenantUser> {
    try {
      // Utiliser l'endpoint spécialisé add-user-to-tenant
      const response = await api.post(`${this.baseUrl}add-user-to-tenant/`, data); 
      return response.data.data || response.data;
    } catch (error: any) {
      
      // Gestion des erreurs de validation
      if (error.response?.status === 400) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          const errorMessages = Object.values(errors).flat();
          throw new Error(`Erreur de validation: ${errorMessages.join(', ')}`);
        }
      }
      
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'ajout de l\'utilisateur au tenant');
    }
  }

  /**
   * Récupérer les utilisateurs d'un tenant
   */
  async getTenantUsers(tenantId: number): Promise<TenantUser[]> {
    try {
      
      const response = await api.get(`/platform/debug/tenant-users/${tenantId}/`);
     
      // L'endpoint de diagnostic retourne {tenant_users: [...]}
      let users = response.data.tenant_users || [];
      
      
      if (Array.isArray(users)) {
        return users;
      } else if (users && typeof users === 'object') {
        
        // Essayer de trouver un tableau dans l'objet
        for (const key of Object.keys(users)) {
          if (Array.isArray(users[key])) {
            
            return users[key];
          }
        }
      }
      
      console.warn('⚠️ Aucun tableau d\'utilisateurs trouvé dans la réponse');
      return [];
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des utilisateurs du tenant:', error);
      console.error('❌ Détails de l\'erreur:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      });
      
      // Si c'est une erreur 401, vérifier l'authentification
      if (error.response?.status === 401) {
        console.error('🔑 Erreur d\'authentification - Token invalide ou expiré');
        const token = localStorage.getItem('access_token');
        console.error('🔑 Token actuel:', token ? 'Présent' : 'Absent');
      }
      
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des utilisateurs du tenant');
    }
  }

  /**
   * Supprimer un utilisateur d'un tenant
   */
  async removeUserFromTenant(tenantUserId: number): Promise<void> {
    try {
      
      await api.delete(`${this.baseUrl}${tenantUserId}/`);
      
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression de l\'utilisateur du tenant:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur du tenant');
    }
  }

  /**
   * Mettre à jour le rôle d'un utilisateur dans un tenant
   */
  async updateUserRole(tenantUserId: number, role: 'TENANT_ADMIN' | 'TENANT_MANAGER'): Promise<TenantUser> {
    try {
      
      const response = await api.patch(`${this.baseUrl}${tenantUserId}/`, { role });
      
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour du rôle:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du rôle');
    }
  }

  /**
   * Récupérer les détails d'un utilisateur de tenant
   */
  async getTenantUser(tenantUserId: number): Promise<TenantUser> {
    try {
      
      const response = await api.get(`${this.baseUrl}${tenantUserId}/`);
      
      return response.data.data || response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des détails:', error);
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des détails');
    }
  }

  /**
   * Récupérer les informations de l'utilisateur tenant connecté
   */
  async getCurrentTenantUser(): Promise<TenantUser | null> {
    try {
      
      const response = await api.get(`${this.baseUrl}current-user/`);
      
      return response.data.data || response.data;
    } catch (error: any) {
      // L'erreur 404 est normale si l'utilisateur n'est pas un utilisateur de tenant
      if (error.response?.status === 404) {
        return null;
      }
      
      // Pour les autres erreurs, afficher un log d'erreur
      console.error('❌ Erreur lors de la récupération de l\'utilisateur tenant connecté:', error);
      return null;
    }
  }
}

const tenantUserService = new TenantUserService();
export default tenantUserService;
